import { useEffect, useRef, useState } from "react";
import * as ProfileService from "../Modules/Profile/Service/ProfileService";
import Spinner from "../components/Spinner";
import { useTranslation } from "react-i18next";
import { toastMsg } from "../utils/swal";

const OtpForm = ({ onSubmit, onCancel, phone, email, preventReload = false }) => {
    const { t } = useTranslation();
    const [confirmOtp, setConfirmOtp] = useState({
        otp: "",
        extra_phone_number: phone,
    });
    const [otpDigits, setOtpDigits] = useState(["", "", "", ""]);
    const [loading, setLoading] = useState(false);
    const [tryAgain, setTryAgain] = useState(false);
    const [countdown, setCountdown] = useState(120);
    const inputRefs = useRef([]);
    const timerRef = useRef(null);
    const countdownRef = useRef(120);

    const startCountdown = () => {
        clearInterval(timerRef.current);
        countdownRef.current = 120;
        setCountdown(120);
        setTryAgain(false);
        timerRef.current = setInterval(() => {
            countdownRef.current -= 1;
            setCountdown(countdownRef.current);
            if (countdownRef.current <= 0) {
                clearInterval(timerRef.current);
                setTryAgain(true);
            }
        }, 1000);
    };

    const sendOtp = async () => {
        setLoading(true);
        setOtpDigits(["", "", "", ""]);
        if (phone) {
            setConfirmOtp({
                otp: "",
                extra_phone_number: phone,
            });
        } else {
            setConfirmOtp({
                otp: "",
                extra_email: email.email,
            });
        }
        try {
            if (phone) {
                await ProfileService.sendOtpToPhone({
                    extra_phone_number: phone,
                });
            } else {
                await ProfileService.sendVerifyOtpToEmail({
                    extra_email: email.email,
                });
            }
            startCountdown();
        } catch (err) {
            
            toastMsg(t("errors.systemError"), "#ff0000", "#ffffff");
            onCancel();
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        sendOtp();
        return () => clearInterval(timerRef.current);
    }, [phone, email]);



    const verifyOtp = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            if (email) {
                await ProfileService.verifyEmailOtpConfirmation(confirmOtp);
            } else {
                await ProfileService.otpConfirmation(confirmOtp);
            }
            onSubmit(true);
            toastMsg(t("otpForm.otpSubmitted"), "#00ff00", "#ffffff");
            if (!preventReload) {
                window.location.reload();
            }
        } catch (err) {
            onSubmit(false);
            setTryAgain(true);
            toastMsg(t("otpForm.otpFailed"), "#ff0000", "#ffffff");
        } finally {
            setLoading(false);
        }
    };

    const formatTime = (seconds) => {
        const mins = String(Math.floor(seconds / 60)).padStart(2, "0");
        const secs = String(seconds % 60).padStart(2, "0");
        return `${mins}:${secs}`;
    };

    const handleResendClick = () => {
        sendOtp();
    };

    return (
        <div className="otpForm">
            <div className="overlay" onClick={onCancel}></div>
            <div className="content">
                <div className="titleBox">
                    <h4 className="modalTitle">OTP</h4>
                    <p className="info">
                        {t("otpForm.otp")} (OTP){" "}
                        <span>
                            {phone
                                ? phone.replace(/(\+\d{1,3})\d{7}(\d{2})/, "$1******$2")
                                : email.email.replace(/^[^@]+/, "*********")}
                        </span>{" "}
                        {phone ? t("otpForm.phone") : t("otpForm.mail")} {t("otpForm.sent")}
                    </p>
                </div>
                {loading ? (
                    <Spinner />
                ) : (
                    <>
                        <div className="inputBox">
                            <div className="inputArea">
                                {[...Array(4)].map((_, index) => (
                                    <input
                                        key={index}
                                        disabled={tryAgain}
                                        type="tel"
                                        inputMode="numeric"
                                        autoComplete="one-time-code"
                                        pattern="[0-9]*"
                                        maxLength="1"
                                        ref={(el) => (inputRefs.current[index] = el)}
                                        value={otpDigits[index]}
                                        onPaste={(e) => {
                                            const text = e.clipboardData.getData("text").trim();
                                            if (!/^\d+$/.test(text)) return;
                                            
                                            e.preventDefault();
                                            const digits = text.split("").slice(0, 4);
                                            const newOtpDigits = [...otpDigits];
                                            
                                            digits.forEach((digit, i) => {
                                                if (i < 4) newOtpDigits[i] = digit;
                                            });

                                            setOtpDigits(newOtpDigits);
                                            setConfirmOtp((prev) => ({
                                                ...prev,
                                                otp: newOtpDigits.join(""),
                                            }));

                                            // Focus appropriate input or submit button
                                            const nextIndex = Math.min(digits.length, 3);
                                            inputRefs.current[nextIndex]?.focus();
                                            
                                            if (digits.length === 4) {
                                                document.getElementById("submit-button")?.focus();
                                            }
                                        }}
                                        onFocus={(e) => {
                                            e.target.style.backgroundColor = "#fff";
                                            e.target.select();
                                        }}
                                        onBlur={(e) => (e.target.style.backgroundColor = "#f1f5f9")}
                                        onKeyDown={(e) => {
                                            if (e.key === "Backspace" || e.key === "Delete") {
                                                if (!otpDigits[index] && index > 0) {
                                                    inputRefs.current[index - 1]?.focus();
                                                }
                                            }
                                        }}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            if (!/^[0-9]?$/.test(value)) return;

                                            const newOtpDigits = [...otpDigits];
                                            newOtpDigits[index] = value;
                                            setOtpDigits(newOtpDigits);
                                            setConfirmOtp((prev) => ({
                                                ...prev,
                                                otp: newOtpDigits.join(""),
                                            }));

                                            // Auto-focus to next input when digit is entered
                                            if (value && index < 3) {
                                                inputRefs.current[index + 1]?.focus();
                                            } else if (value && index === 3) {
                                                // Focus submit button when last digit is entered
                                                const submitButton = document.getElementById("submit-button");
                                                if (submitButton) submitButton.focus();
                                            }
                                        }}
                                    />
                                ))}
                            </div>
                        </div>
                        {tryAgain ? (
                            <p className="details try" onClick={handleResendClick}>
                                {t("otpForm.sendAgain")}
                            </p>
                        ) : (
                            <p className="details">
                                <span className="time">{formatTime(countdown)}</span>{' '}
                                {t("otpForm.canSendAgain")}
                            </p>
                        )}
                        <div className="btnGroup">
                            <button type="button" className="btn cancel" onClick={onCancel}>
                                {t("otpForm.cancel")}
                            </button>
                            <button
                                disabled={confirmOtp?.otp?.length < 4 || tryAgain}
                                id="submit-button"
                                type="submit"
                                className="btn"
                                onClick={verifyOtp}
                            >
                                {t("otpForm.submit")}
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default OtpForm;
